import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import * as Sentry from '@sentry/react';
import { Wordmark } from '@wundo/ui';
import { signInDestination } from '@wundo/client/auth.ts';
import { redirectIfSignedIn } from '@wundo/client/session.ts';
import { SigningIn } from '@/components/SigningIn.tsx';
import { SentConfirmation } from '@/components/login/SentConfirmation.tsx';
import { SignInChoices } from '@/components/login/SignInChoices.tsx';
import { refusedSignInMessage } from '@/components/login/refusedSignInMessage.ts';
import { signInReturnTo } from '@/lib/signInReturnTo.ts';

/*
 * The only login screen. The staff console has none of its own — it sends
 * people here with `redirect` set to its own origin, and sign-in returns them
 * there. The session cookie is set on the API origin, which both frontends
 * call, so one sign-in serves both.
 *
 * The value is checked here, because a signed-in visitor is sent straight on
 * to it, and again by the backend before it ends up in a token.
 */
export const Route = createFileRoute('/login')({
  component: LoginRoute,
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: signInReturnTo(search.redirect),
    error: typeof search.error === 'string' ? search.error : undefined,
  }),
  /*
   * Someone who already has a session has no use for this screen, so they are
   * sent on to where they were going instead of being asked to sign in again —
   * the same destination sign-in itself would have used.
   */
  beforeLoad: async ({ context, search }) => {
    /*
     * Signing out and an expired session both arrive here without a reload,
     * and the recording is for signed-in use only. The pending segment is
     * dropped so this screen is never uploaded.
     */
    void Sentry.getReplay()?.stop({ flush: false });

    /*
     * A used link is said as such, also to a browser still signed in from it:
     * sent on, a session that may only practise meets a page about Teams.
     */
    if (search.error === 'INVALID_TOKEN') {
      return { signedIn: false };
    }

    return {
      signedIn: await redirectIfSignedIn(
        context.queryClient,
        signInDestination(search.redirect),
      ),
    };
  },
});

/* Reading the search is the route's job, so the screen itself stays renderable. */
function LoginRoute() {
  const { redirect, error } = Route.useSearch();
  const { signedIn } = Route.useRouteContext();

  /* The browser is on its way out; the form would be a flash of the wrong screen. */
  if (signedIn) {
    return <SigningIn />;
  }

  return <LoginPage redirect={redirect} error={error} />;
}

function BrandPanel() {
  return (
    <div className='relative flex flex-col overflow-hidden bg-navy px-14 py-12 text-white max-agenda:hidden'>
      {/* Glow tuned to the design's 55% setting; see .login-brand-glow in app.css. */}
      <div className='login-brand-glow pointer-events-none absolute inset-0' />
      <div className='relative z-10 m-auto flex flex-col gap-6.5'>
        <img src='/logo-green.png' alt='Wundo' className='block h-auto w-40' />
        <span className='h-0.5 w-22 rounded-full bg-green' />
      </div>
    </div>
  );
}

export function LoginPage({
  redirect,
  error: errorCode,
}: {
  redirect?: string;
  error?: string;
}) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);

  return (
    <div className='grid h-dvh grid-cols-2 overflow-hidden max-agenda:grid-cols-1'>
      <BrandPanel />
      <div className='flex items-center justify-center overflow-y-auto px-7 py-10'>
        <div className='flex w-full max-w-98 flex-col'>
          <div className='mb-7.5 hidden justify-center max-agenda:flex'>
            <Wordmark registered className='h-8.5' />
          </div>

          {sent ? (
            <SentConfirmation email={email} onReset={() => setSent(false)} />
          ) : (
            <SignInChoices
              redirect={redirect}
              refusal={refusedSignInMessage(errorCode)}
              email={email}
              onEmailChange={setEmail}
              onSent={() => setSent(true)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
